import React, { useEffect, useState, useMemo, useRef } from "react";
import { SharedLoading, SharedFeatureError } from "../../../shared";
import {
  useGetConnectDirectoryQuery,
  useGetMyConnectProfileQuery,
  useGetRolodexNotesQuery,
  useSaveRolodexNoteMutation,
} from "../../../../store/slices/api";
import { selectUser } from "../../../../store/slices/authentication";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import DirectoryRow from "./DirectoryRow";
import AlphabetJumpBar from "./AlphabetJumpBar";
import { normalizeName, squashName, nameMatches } from "../../../shared/nameSearch";

// Strips accents so "É" matches "e", etc.
const normalize = (str) =>
  (str || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

function Directory() {
  const user = useSelector(selectUser);
  const individualId = parseInt(user.externalId);
  const [searchParams, setSearchParams] = useSearchParams();

  const { data: myProfile } = useGetMyConnectProfileQuery(individualId);
  const { data: directory, isLoading, isError, refetch } =
    useGetConnectDirectoryQuery(individualId);
  const { data: savedNotes, isError: notesFailed } =
    useGetRolodexNotesQuery(individualId);
  const rolodexNotes = savedNotes || {};
  // Once notes have arrived they stay usable, even if a later reload fails.
  const notesState =
    savedNotes !== undefined ? "ready" : notesFailed ? "failed" : "loading";
  const [saveNote] = useSaveRolodexNoteMutation();

  const [searchText, setSearchText] = useState("");
  const [stateFilter, setStateFilter] = useState("");
  const [sortBy, setSortBy] = useState("last"); // "first" or "last"
  const [expandedId, setExpandedId] = useState(null);
  const openedFromLink = useRef(false);

  // Arriving from a link elsewhere on the site — a poster's name on a Scrapbook
  // post is "/connect?tab=directory&person=123" — opens that person's card and
  // scrolls to it, once, so a later collapse is not undone on every render.
  const askedFor = parseInt(searchParams.get("person"), 10);
  useEffect(() => {
    if (!askedFor || openedFromLink.current || !directory) return;
    openedFromLink.current = true;
    setExpandedId(askedFor);
    const row = document.getElementById(`directory-person-${askedFor}`);
    if (row && row.scrollIntoView) row.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [askedFor, directory]);

  const handleToggle = (individualId) => {
    setExpandedId((prev) => (prev === individualId ? null : individualId));
  };

  // Filter, sort, and group entries by letter
  const { groupedEntries, totalCount, availableLetters } = useMemo(() => {
    if (!directory) return { groupedEntries: {}, totalCount: 0, availableLetters: [] };

    const normalizedSearch = normalize(searchText);
    const normalizedState = normalize(stateFilter);

    // Filter
    const filtered = directory.filter((entry) => {
      // searchable_married_name is the name a woman married into — included so
      // she can be found under it as well as her maiden name. Search only; the
      // directory still lists and sorts by the recorded legal name.
      const nameToSearch = [
        entry.legal_name,
        entry.profile?.preferred_name,
        entry.searchable_married_name,
      ]
        .filter(Boolean)
        .join(" ");
      // Ignores accents, apostrophe style and spaces — comparing only.
      const matchesSearch =
        !normalizedSearch ||
        nameMatches(searchText, normalizeName(nameToSearch), squashName(nameToSearch));
      const matchesState =
        !normalizedState ||
        normalize(entry.profile?.state_of_residency).includes(normalizedState);
      return matchesSearch && matchesState;
    });

    // Sort
    const sorted = [...filtered].sort((a, b) => {
      if (sortBy === "last") {
        return (a.surname || "").localeCompare(b.surname || "") ||
               (a.forenames || "").localeCompare(b.forenames || "");
      }
      return (a.forenames || "").localeCompare(b.forenames || "") ||
             (a.surname || "").localeCompare(b.surname || "");
    });

    // Group by first letter
    const groups = {};
    for (const entry of sorted) {
      const nameForLetter =
        sortBy === "last" ? entry.surname : entry.forenames;
      const letter = (nameForLetter || "?").charAt(0).toUpperCase();
      // Normalize accented letters to base letter for grouping
      const normalizedLetter = normalize(letter).toUpperCase() || "#";
      if (!groups[normalizedLetter]) groups[normalizedLetter] = [];
      groups[normalizedLetter].push(entry);
    }

    const letters = Object.keys(groups).sort((a, b) => {
      const aIsLetter = /^[A-Z]$/.test(a);
      const bIsLetter = /^[A-Z]$/.test(b);
      if (aIsLetter && !bIsLetter) return -1;
      if (!aIsLetter && bIsLetter) return 1;
      return a.localeCompare(b);
    });
    return {
      groupedEntries: groups,
      totalCount: filtered.length,
      availableLetters: letters,
    };
  }, [directory, searchText, stateFilter, sortBy]);

  if (isLoading) {
    return <SharedLoading text="Loading directory..." />;
  }

  // Friendly retry on error — never the misleading "No family members match".
  if (isError) {
    return <SharedFeatureError onRetry={refetch} />;
  }

  return (
    <div className="flex flex-col gap-y-[16px]">
      {/* Soft banner if viewer has no profile */}
      {!myProfile && (
        <div className="rounded-[12px] bg-dupontBlue/5 dark:bg-darkGold/[0.06] border border-dupontBlue/20 dark:border-darkGold/10 p-[16px] flex items-center justify-between flex-wrap gap-[12px]">
          <p className="text-[14px] font-body text-dupontBlue dark:text-darkText">
            Create your profile to see more details about your cousins.
          </p>
          <button
            type="button"
            className="rounded-full px-[14px] py-[8px] bg-dupontGold text-white text-sm font-medium cursor-pointer"
            onClick={() =>
              setSearchParams({ tab: "myProfile" }, { replace: true })
            }
          >
            Create My Profile
          </button>
        </div>
      )}

      {/* Search, filter, and sort bar — sticks to top while scrolling. On a
          phone the three wrap onto their own lines instead of running off. */}
      <div className="sticky top-0 z-20 bg-dupontCream/95 dark:bg-darkPrimary/95 backdrop-blur-sm py-[12px] px-[12px] sm:px-0 shadow-[0_4px_12px_rgba(0,9,28,0.06)] dark:shadow-[0_4px_12px_rgba(0,0,0,0.3)] rounded-b-[12px] flex flex-row flex-wrap gap-x-[16px] gap-y-[10px] items-center justify-center">
        {/* Left: Search by name */}
        <input
          type="text"
          placeholder="Search by name"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          className="px-[18px] py-[10px] rounded-full border-2 border-dupontGold bg-white dark:bg-darkCard text-[14px] font-display text-dupontBlue dark:text-darkText placeholder:text-dupontBlue/80 dark:placeholder:text-darkText/70 outline-none focus:shadow-[0_2px_8px_rgba(0,9,28,0.12)] transition-all duration-200 w-full sm:w-[200px]"
        />

        {/* Center: Sort toggle button */}
        <button
          className="px-[18px] py-[10px] rounded-full border-2 border-dupontGold bg-white dark:bg-darkCard text-[14px] font-display font-bold text-dupontBlue/70 dark:text-darkTextSecondary cursor-pointer select-none transition-all duration-200 hover:bg-dupontGold hover:text-white hover:shadow-[0_2px_8px_rgba(0,9,28,0.12)] whitespace-nowrap"
          onClick={() => setSortBy((s) => (s === "first" ? "last" : "first"))}
        >
          Sort: {sortBy === "first" ? "First Name" : "Last Name"}
        </button>

        {/* Right: Filter by state */}
        <input
          type="text"
          placeholder="Filter by state"
          value={stateFilter}
          onChange={(e) => setStateFilter(e.target.value)}
          className="px-[18px] py-[10px] rounded-full border-2 border-dupontGold bg-white dark:bg-darkCard text-[14px] font-display text-dupontBlue dark:text-darkText placeholder:text-dupontBlue/80 dark:placeholder:text-darkText/70 outline-none focus:shadow-[0_2px_8px_rgba(0,9,28,0.12)] transition-all duration-200 w-full sm:w-[200px]"
        />
      </div>

      {/* Results count */}
      <div className="text-[15px] font-body font-medium text-dupontBlue/80 dark:text-darkText/70">
        {totalCount.toLocaleString()} family{" "}
        {totalCount === 1 ? "member" : "members"}
      </div>

      {/* Side-by-side: Alphabet jump bar (left) + Letter cards (right) */}
      <div className="flex flex-row gap-x-[12px]">
        {/* Alphabet jump bar — sticky sidebar on the left */}
        {availableLetters.length > 1 && (
          <div className="sticky top-[80px] self-start hidden sm:block">
            <AlphabetJumpBar availableLetters={availableLetters} />
          </div>
        )}

        {/* Letter-grouped cards */}
        <div className="flex-1 flex flex-col gap-y-[16px]">
          {availableLetters.length > 0 ? (
            availableLetters.map((letter) => (
              <div
                key={letter}
                className="rounded-[12px] border border-dupontCreamDk/60 dark:border-darkGold/10 shadow-[0_8px_30px_rgba(0,9,28,0.12),0_2px_8px_rgba(0,9,28,0.06)] dark:shadow-[0_8px_30px_rgba(0,0,0,0.3)] overflow-hidden bg-white dark:bg-darkCard"
              >
                {/* Sticky letter header */}
                <div
                  id={`letter-${letter}`}
                  className="sticky top-0 z-[5] bg-dupontBlue dark:bg-darkAccentBg px-[16px] py-[10px] text-[18px] font-display font-extrabold text-dupontGold dark:text-darkGold tracking-wide rounded-t-[12px] flex items-center justify-between"
                >
                  <span>{letter}</span>
                  <span className="font-body italic font-semibold text-[17px] text-dupontGold dark:text-darkGold/80 tracking-normal">Rolodex Notes</span>
                </div>

                {/* Rows for this letter */}
                {groupedEntries[letter].map((entry) => (
                  <DirectoryRow
                    key={entry.individual_id}
                    rowId={`directory-person-${entry.individual_id}`}
                    entry={entry}
                    isExpanded={expandedId === entry.individual_id}
                    onToggle={handleToggle}
                    note={rolodexNotes[entry.individual_id] || ""}
                    notesState={notesState}
                    onSaveNote={(text) => saveNote({
                      owner_individual_id: individualId,
                      target_individual_id: entry.individual_id,
                      note: text,
                    })}
                  />
                ))}
              </div>
            ))
          ) : (
            <div className="rounded-[12px] border border-dupontCreamDk/60 dark:border-darkGold/10 shadow-[0_8px_30px_rgba(0,9,28,0.12),0_2px_8px_rgba(0,9,28,0.06)] dark:shadow-[0_8px_30px_rgba(0,0,0,0.3)] bg-white dark:bg-darkCard text-center text-dupontBlue/50 dark:text-darkTextTertiary py-[40px] text-[15px] font-body">
              No family members match your search.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Directory;
